import { initializePaddle, type Paddle } from '@paddle/paddle-js';

let cachedPaddle: Paddle | undefined;
/** Token + resolved JS environment (must match or Paddle returns 403 on transaction-checkout). */
let cacheKey: string | undefined;

/**
 * Client tokens are prefixed <c>test_</c> (sandbox) or <c>live_</c> (production). Prefer that over the
 * server <c>sandbox</c> flag so dev overrides and API stay aligned with the price catalog environment.
 */
function inferPaddleJsEnvironment(token: string, serverSandbox: boolean): 'sandbox' | 'production' {
  const s = token.trim();
  if (s.startsWith('live_')) return 'production';
  if (s.startsWith('test_')) return 'sandbox';
  return serverSandbox ? 'sandbox' : 'production';
}

async function getPaddleInstance(token: string, serverSandbox: boolean): Promise<Paddle | undefined> {
  const environment = inferPaddleJsEnvironment(token, serverSandbox);
  const nextKey = `${token}\0${environment}`;
  if (cacheKey !== nextKey) {
    cachedPaddle = undefined;
    cacheKey = undefined;
  }
  if (cachedPaddle && cacheKey === nextKey && cachedPaddle.Initialized) return cachedPaddle;
  cachedPaddle = await initializePaddle({
    token,
    environment,
  });
  cacheKey = nextKey;
  return cachedPaddle;
}

export async function openPaddleSubscriptionCheckout(params: {
  clientToken: string;
  sandbox: boolean;
  priceId: string;
  customerEmail: string;
  tenantId: string;
  successUrl?: string;
}): Promise<void> {
  const tok = params.clientToken.trim();
  if (tok.startsWith('live_') && params.sandbox) {
    throw new Error(
      'Paddle checkout misconfiguration: client token is live (live_…) but sandbox mode is on. Use a sandbox test_ token, or remove REACT_APP_PADDLE_DEV_CLIENT_TOKEN so the API mints a token with your sandbox API key.',
    );
  }
  if (tok.startsWith('test_') && !params.sandbox) {
    throw new Error(
      'Paddle checkout misconfiguration: client token is sandbox (test_…) but sandbox mode is off. Set REACT_APP_PADDLE_DEV_SANDBOX=true or use a live token with production prices only.',
    );
  }

  const paddle = await getPaddleInstance(params.clientToken, params.sandbox);
  if (!paddle) throw new Error('Paddle failed to initialize');

  try {
    paddle.Checkout.open({
      items: [{ priceId: params.priceId, quantity: 1 }],
      customer: { email: params.customerEmail },
      customData: { tenant_id: params.tenantId },
      settings: params.successUrl ? { successUrl: params.successUrl } : undefined,
    });
  } catch (e: unknown) {
    const m = e instanceof Error ? e.message : String(e);
    throw new Error(m || 'Paddle checkout failed to open');
  }
}
